import React, { useRef, useCallback } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  disabled?: boolean;
}

const toolbarStyles: React.CSSProperties = {
  display: 'flex',
  gap: '2px',
  padding: '4px 6px',
  borderBottom: '1px solid #ddd',
  backgroundColor: '#fafafa',
  borderRadius: '4px 4px 0 0',
  flexWrap: 'wrap',
};

const btnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid transparent',
  borderRadius: '4px',
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#333',
  lineHeight: 1,
  minWidth: '28px',
  textAlign: 'center',
};

const editorStyles: React.CSSProperties = {
  padding: '8px 10px',
  minHeight: '100px',
  outline: 'none',
  fontSize: '0.9rem',
  lineHeight: 1.5,
  color: '#333',
  overflowY: 'auto',
};

/**
 * Lightweight rich text editor using contentEditable.
 * Supports: Bold, Italic, Underline, Bullet List, Link.
 * Outputs HTML via onChange.
 */
export function RichTextEditor({ value, onChange, placeholder = 'Type here...', minHeight = '100px', disabled = false }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  const exec = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    // Trigger onChange after command
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    editorRef.current?.focus();
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      exec('createLink', url);
    }
  }, [exec]);

  return (
    <div style={{ border: '1px solid #ccc', borderRadius: '6px', overflow: 'hidden', opacity: disabled ? 0.6 : 1 }}>
      {/* Toolbar */}
      <div style={toolbarStyles}>
        <button type="button" style={btnStyle} onClick={() => exec('bold')} title="Bold" aria-label="Bold" disabled={disabled}>
          <strong>B</strong>
        </button>
        <button type="button" style={btnStyle} onClick={() => exec('italic')} title="Italic" aria-label="Italic" disabled={disabled}>
          <em>I</em>
        </button>
        <button type="button" style={btnStyle} onClick={() => exec('underline')} title="Underline" aria-label="Underline" disabled={disabled}>
          <u>U</u>
        </button>
        <button type="button" style={{ ...btnStyle, fontSize: '0.75rem' }} onClick={() => exec('insertUnorderedList')} title="Bullet List" aria-label="Bullet list" disabled={disabled}>
          • List
        </button>
        <button type="button" style={{ ...btnStyle, fontSize: '0.75rem' }} onClick={handleLink} title="Insert Link" aria-label="Insert link" disabled={disabled}>
          🔗 Link
        </button>
      </div>
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        style={{ ...editorStyles, minHeight }}
        onInput={handleInput}
        onBlur={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-label="Rich text editor"
      />
    </div>
  );
}
